import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, Pie } from 'react-chartjs-2';
import {
  ArcElement, BarElement, CategoryScale, Chart, LinearScale, Tooltip, Legend,
} from 'chart.js';
import { reports, studies, type OfferReport, type ReportStatusBucket, type ReportOfferRef } from '@/api/endpoints';
import type { OfferStatus, StudyType } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { formatMoney } from '@/lib/money';

Chart.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const fmtIsoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDate = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('pl-PL', { dateStyle: 'medium' }).format(d);
};

interface Preset {
  id: string;
  label: string;
  range: () => { from: string; to: string };
}

const presets: Preset[] = [
  {
    id: 'this-month',
    label: 'Ten miesiąc',
    range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: fmtIsoDate(from), to: fmtIsoDate(to) };
    },
  },
  {
    id: 'last-month',
    label: 'Poprzedni miesiąc',
    range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: fmtIsoDate(from), to: fmtIsoDate(to) };
    },
  },
  {
    id: 'this-quarter',
    label: 'Ten kwartał',
    range: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      const from = new Date(now.getFullYear(), q * 3, 1);
      const to = new Date(now.getFullYear(), q * 3 + 3, 0);
      return { from: fmtIsoDate(from), to: fmtIsoDate(to) };
    },
  },
  {
    id: 'last-quarter',
    label: 'Poprzedni kwartał',
    range: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3) - 1;
      const yearOffset = q < 0 ? -1 : 0;
      const qIdx = q < 0 ? q + 4 : q;
      const from = new Date(now.getFullYear() + yearOffset, qIdx * 3, 1);
      const to = new Date(now.getFullYear() + yearOffset, qIdx * 3 + 3, 0);
      return { from: fmtIsoDate(from), to: fmtIsoDate(to) };
    },
  },
  {
    id: 'this-year',
    label: 'Ten rok',
    range: () => {
      const y = new Date().getFullYear();
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    },
  },
  {
    id: 'last-year',
    label: 'Poprzedni rok',
    range: () => {
      const y = new Date().getFullYear() - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    },
  },
];

const STATUS_META: Record<OfferStatus, { label: string; color: string; iconBg: string; icon: string; chartColor: string }> = {
  DRAFT:     { label: 'Szkice',         color: 'text-ink-600',    iconBg: 'bg-ink-100',    icon: 'file-text', chartColor: '#94a3b8' },
  SENT:      { label: 'Wystawione',     color: 'text-water-700',  iconBg: 'bg-water-100',  icon: 'file-text', chartColor: '#06b6d4' },
  ACCEPTED:  { label: 'Zaakceptowane',  color: 'text-green-700',  iconBg: 'bg-green-100',  icon: 'check',     chartColor: '#16a34a' },
  ORDERED:   { label: 'Zlecenia',       color: 'text-emerald-700',iconBg: 'bg-emerald-100',icon: 'check',     chartColor: '#059669' },
  REJECTED:  { label: 'Odrzucone',      color: 'text-red-700',    iconBg: 'bg-red-100',    icon: 'x',         chartColor: '#dc2626' },
  EXPIRED:   { label: 'Przeterminowane',color: 'text-amber-700',  iconBg: 'bg-amber-100',  icon: 'bell',      chartColor: '#d97706' },
  CONVERTED: { label: 'Konwersja na umowę', color: 'text-purple-700', iconBg: 'bg-purple-100', icon: 'check', chartColor: '#9333ea' },
};

// Kolejność wyświetlania KPI — najistotniejsze statusy pierwsze
const STATUS_ORDER: OfferStatus[] = ['SENT', 'ACCEPTED', 'ORDERED', 'REJECTED', 'EXPIRED', 'CONVERTED', 'DRAFT'];

// Polskie skrócone nazwy miesięcy do wykresu słupkowego
const MIESIACE_KROTKIE = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

function fmtMonthLabel(ym: string): string {
  // ym format: "2026-03"
  const [year, month] = ym.split('-');
  const m = parseInt(month, 10);
  if (Number.isNaN(m) || m < 1 || m > 12) return ym;
  return `${MIESIACE_KROTKIE[m - 1]} ${year.slice(2)}`;
}

export function Raporty() {
  const navigate = useNavigate();
  const [from, setFrom] = useState<string>(() => presets[0].range().from);
  const [to, setTo] = useState<string>(() => presets[0].range().to);
  const [activePreset, setActivePreset] = useState<string | null>('this-month');
  const [studyType, setStudyType] = useState<string>('');           // '' = wszystkie typy
  const [studyTypes, setStudyTypes] = useState<StudyType[]>([]);
  const [data, setData] = useState<OfferReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<OfferStatus>>(new Set());

  const toggleExpanded = (status: OfferStatus) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const load = (f: string, t: string, st: string) => {
    setLoading(true);
    setErr(null);
    reports.offers(f, t, st || undefined)
      .then(setData)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  };

  // Auto-fetch na starcie + wczytanie listy typów opracowań do dropdownu
  useEffect(() => {
    load(from, to, studyType);
    studies.types().then(setStudyTypes).catch(() => { /* nonfatal */ });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const applyPreset = (p: Preset) => {
    const r = p.range();
    setFrom(r.from);
    setTo(r.to);
    setActivePreset(p.id);
    load(r.from, r.to, studyType);
  };

  const onCustomChange = (which: 'from' | 'to', value: string) => {
    setActivePreset(null);
    if (which === 'from') setFrom(value);
    else setTo(value);
  };

  const onStudyTypeChange = (value: string) => {
    setStudyType(value);
    load(from, to, value);
  };

  const generate = () => {
    if (!from || !to) return;
    load(from, to, studyType);
  };

  const selectedStudyTypeLabel = useMemo(() => {
    if (!studyType) return null;
    const t = studyTypes.find(s => s.id === studyType);
    return t ? `${t.abbreviation}-${t.code} — ${t.name}` : studyType;
  }, [studyType, studyTypes]);

  const acceptanceRate = useMemo(() => {
    if (!data) return null;
    const accepted = data.byStatus.ACCEPTED.count + data.byStatus.CONVERTED.count;
    const decided = accepted + data.byStatus.REJECTED.count + data.byStatus.EXPIRED.count;
    if (decided === 0) return null;
    return Math.round((accepted / decided) * 100);
  }, [data]);

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Raporty</h1>
          <div className="text-ink-400 text-[13px]">
            Podsumowanie ofert według statusu w wybranym zakresie dat.
          </div>
        </div>
      </header>

      {/* Wybór zakresu dat */}
      <div className="card mb-4">
        <div className="card-head">
          <h3 className="text-[13px] font-semibold text-ink-700">Zakres dat (po dacie wystawienia oferty)</h3>
        </div>
        <div className="card-body space-y-3">
          <div className="flex flex-wrap gap-2">
            {presets.map(p => (
              <button
                key={p.id}
                onClick={() => applyPreset(p)}
                className={'btn btn-sm ' + (activePreset === p.id ? 'btn-primary' : 'btn-secondary')}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-ink-100">
            <div>
              <label className="block text-[11px] text-ink-500 mb-1">Od</label>
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={e => onCustomChange('from', e.target.value)}
                className="input input-sm w-44"
              />
            </div>
            <div>
              <label className="block text-[11px] text-ink-500 mb-1">Do</label>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={e => onCustomChange('to', e.target.value)}
                className="input input-sm w-44"
              />
            </div>
            <div>
              <label className="block text-[11px] text-ink-500 mb-1">Typ opracowania</label>
              <SearchableSelect
                value={studyType}
                onChange={onStudyTypeChange}
                className="select-sm min-w-[280px]"
              >
                <option value="">Wszystkie typy</option>
                {studyTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.abbreviation}-{t.code} — {t.name}</option>
                ))}
              </SearchableSelect>
            </div>
            <button onClick={generate} className="btn btn-primary btn-sm" disabled={loading || !from || !to}>
              <Icon name="check" className="w-3.5 h-3.5"/> Generuj raport
            </button>
            {data && !loading && (
              <div className="text-[12px] text-ink-500 ml-auto">
                Zakres: <span className="font-semibold text-ink-700">{fmtDate(data.from)}</span>
                {' – '}
                <span className="font-semibold text-ink-700">{fmtDate(data.to)}</span>
                {selectedStudyTypeLabel && (
                  <>
                    <br/>
                    <span className="text-ink-400">Typ:</span>{' '}
                    <span className="font-semibold text-ink-700">{selectedStudyTypeLabel}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {err && <div className="text-red-500 p-3 mb-3">Błąd: {err}</div>}
      {loading && <div className="text-ink-400 p-3 mb-3">Generowanie raportu…</div>}

      {data && !loading && (
        <>
          {/* Podsumowanie */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <SummaryTile
                  label="Łącznie ofert"
                  value={String(data.total.count)}
                  hint="W wybranym zakresie dat"
                />
                <SummaryTile
                  label="Suma netto"
                  value={formatMoney(data.total.totalNet) + ' zł'}
                  hint="Wszystkie statusy"
                />
                <SummaryTile
                  label="Suma brutto"
                  value={formatMoney(data.total.totalGross) + ' zł'}
                  hint={
                    acceptanceRate !== null
                      ? `Skuteczność (zaakceptowane / rozstrzygnięte): ${acceptanceRate}%`
                      : 'Brak rozstrzygnięć w zakresie'
                  }
                />
              </div>
            </div>
          </div>

          {/* KPI per status */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {STATUS_ORDER.map(status => {
              const meta = STATUS_META[status];
              const bucket = data.byStatus[status];
              return <StatusCard key={status} meta={meta} bucket={bucket} />;
            })}
          </div>

          {/* Wykresy: kołowy (status) + słupkowy (czas) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="card lg:col-span-1">
              <div className="card-head">
                <h3 className="text-[13px] font-semibold text-ink-700">Rozkład statusów</h3>
              </div>
              <div className="card-body">
                <StatusPieChart data={data}/>
              </div>
            </div>
            <div className="card lg:col-span-2">
              <div className="card-head">
                <h3 className="text-[13px] font-semibold text-ink-700">Liczba ofert wystawianych w czasie</h3>
                <span className="ml-auto text-[11px] text-ink-400">granulacja: miesiąc</span>
              </div>
              <div className="card-body">
                <MonthlyBarChart data={data}/>
              </div>
            </div>
          </div>

          {/* Tabela szczegółowa */}
          <div className="card">
            <div className="card-head">
              <h3 className="text-[13px] font-semibold text-ink-700">Zestawienie szczegółowe</h3>
              <span className="ml-auto text-[11px] text-ink-400">kliknij wiersz aby rozwinąć listę ofert</span>
            </div>
            <div className="card-body !p-0">
              <table className="table-hw">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th>Status</th>
                    <th className="text-right">Liczba</th>
                    <th className="text-right">Suma netto</th>
                    <th className="text-right">Suma brutto</th>
                    <th className="text-right">Średnia brutto</th>
                  </tr>
                </thead>
                <tbody>
                  {STATUS_ORDER.map(status => {
                    const meta = STATUS_META[status];
                    const b = data.byStatus[status];
                    const avg = b.count > 0 ? b.totalGross / b.count : 0;
                    const canExpand = b.count > 0;
                    const isOpen = expanded.has(status);
                    return (
                      <Fragment key={status}>
                        <tr
                          onClick={() => canExpand && toggleExpanded(status)}
                          className={canExpand ? 'cursor-pointer hover:bg-ink-50' : 'text-ink-400'}
                        >
                          <td className="text-center">
                            {canExpand && (
                              <Icon
                                name="chevron"
                                className={'w-3 h-3 text-ink-400 transition-transform '
                                  + (isOpen ? 'rotate-90' : '')}
                              />
                            )}
                          </td>
                          <td>
                            <span className={'pill ' + meta.iconBg + ' ' + meta.color}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="text-right font-mono tabular-nums text-ink-800">{b.count}</td>
                          <td className="text-right font-mono tabular-nums text-ink-700">
                            {b.totalNet > 0 ? formatMoney(b.totalNet) : '—'}
                          </td>
                          <td className="text-right font-mono tabular-nums text-ink-800 font-semibold">
                            {b.totalGross > 0 ? formatMoney(b.totalGross) : '—'}
                          </td>
                          <td className="text-right font-mono tabular-nums text-ink-500 text-[12px]">
                            {avg > 0 ? formatMoney(avg) : '—'}
                          </td>
                        </tr>
                        {isOpen && b.offers.map(offer => (
                          <OfferDetailRow
                            key={offer.id}
                            offer={offer}
                            onClick={() => navigate(`/oferty/${offer.id}`)}
                          />
                        ))}
                      </Fragment>
                    );
                  })}
                  <tr className="bg-ink-50 font-semibold">
                    <td></td>
                    <td className="text-ink-900">Razem</td>
                    <td className="text-right font-mono tabular-nums text-ink-900">{data.total.count}</td>
                    <td className="text-right font-mono tabular-nums text-ink-900">{formatMoney(data.total.totalNet)}</td>
                    <td className="text-right font-mono tabular-nums text-ink-900">{formatMoney(data.total.totalGross)}</td>
                    <td className="text-right font-mono tabular-nums text-ink-500">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-ink-100 rounded-md p-4 bg-gradient-to-br from-white to-ink-50/30">
      <div className="text-[11px] uppercase tracking-wider text-ink-500 mb-1.5">{label}</div>
      <div className="text-[24px] font-bold text-ink-900 -tracking-[0.01em]">{value}</div>
      {hint && <div className="text-[11px] text-ink-400 mt-1">{hint}</div>}
    </div>
  );
}

function StatusCard({ meta, bucket }: {
  meta: { label: string; color: string; iconBg: string; icon: string; chartColor: string };
  bucket: ReportStatusBucket;
}) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-start gap-3">
          <div className={'w-10 h-10 rounded-md grid place-items-center ' + meta.iconBg + ' ' + meta.color}>
            <Icon name={meta.icon} className="w-4 h-4"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-ink-500">{meta.label}</div>
            <div className="text-[22px] font-bold text-ink-900 -tracking-[0.01em] mt-0.5">{bucket.count}</div>
            <div className="text-[12px] text-ink-500 font-mono tabular-nums">
              {formatMoney(bucket.totalGross)} zł brutto
            </div>
            {bucket.totalNet > 0 && bucket.totalNet !== bucket.totalGross && (
              <div className="text-[11px] text-ink-400 font-mono tabular-nums">
                {formatMoney(bucket.totalNet)} zł netto
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Wiersz z konkretną ofertą — renderowany pod wierszem statusu po rozwinięciu. */
function OfferDetailRow({ offer, onClick }: { offer: ReportOfferRef; onClick: () => void }) {
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer bg-ink-50/40 hover:bg-water-50 border-l-[3px] border-water-200"
      title="Kliknij aby otworzyć ofertę"
    >
      <td></td>
      <td className="pl-2">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[11px] text-ink-500">{offer.number}</span>
          <span className="text-[12px] text-ink-800 truncate max-w-[420px]" title={offer.title}>{offer.title}</span>
        </div>
        <div className="text-[10px] text-ink-400 mt-0.5">
          {offer.clientName ?? '—'}
          {offer.intakeName && <> · ujęcie: {offer.intakeName}</>}
        </div>
      </td>
      <td className="text-right text-[10px] text-ink-400 font-mono">{fmtDate(offer.issueDate)}</td>
      <td className="text-right font-mono tabular-nums text-ink-600 text-[12px]">
        {formatMoney(offer.totalNet)}
      </td>
      <td className="text-right font-mono tabular-nums text-ink-700 text-[12px]">
        {formatMoney(offer.totalGross)}
      </td>
      <td className="text-right">
        <Icon name="chevron" className="w-3 h-3 text-ink-300"/>
      </td>
    </tr>
  );
}

/** Wykres kołowy rozkładu statusów ofert. Pomija statusy z zerową liczbą (czysty wykres). */
function StatusPieChart({ data }: { data: OfferReport }) {
  const slices = STATUS_ORDER
    .map(s => ({ status: s, meta: STATUS_META[s], bucket: data.byStatus[s] }))
    .filter(x => x.bucket.count > 0);

  if (slices.length === 0) {
    return <div className="text-ink-400 text-[12px] py-8 text-center">Brak ofert w wybranym zakresie.</div>;
  }

  const chartData = {
    labels: slices.map(s => s.meta.label),
    datasets: [{
      data: slices.map(s => s.bucket.count),
      backgroundColor: slices.map(s => s.meta.chartColor),
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };

  const totalCount = slices.reduce((sum, s) => sum + s.bucket.count, 0);
  const options = {
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { font: { size: 11 }, boxWidth: 10, padding: 8 },
      },
      tooltip: {
        callbacks: {
          label: (ctx: { label: string; parsed: number }) => {
            const pct = totalCount > 0 ? Math.round((ctx.parsed / totalCount) * 100) : 0;
            return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  return (
    <div style={{ height: 280 }}>
      <Pie data={chartData} options={options}/>
    </div>
  );
}

/** Wykres słupkowy liczby ofert wystawionych w każdym miesiącu raportowanego okresu. */
function MonthlyBarChart({ data }: { data: OfferReport }) {
  if (data.byMonth.length === 0) {
    return <div className="text-ink-400 text-[12px] py-8 text-center">Brak danych do wyświetlenia.</div>;
  }

  const chartData = {
    labels: data.byMonth.map(m => fmtMonthLabel(m.month)),
    datasets: [{
      label: 'Liczba ofert',
      data: data.byMonth.map(m => m.count),
      backgroundColor: '#06b6d4',
      borderColor: '#0891b2',
      borderWidth: 1,
      borderRadius: 4,
    }],
  };

  const options = {
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          afterLabel: (ctx: { dataIndex: number }) => {
            const m = data.byMonth[ctx.dataIndex];
            if (!m) return '';
            return `Suma brutto: ${formatMoney(m.totalGross)} zł`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: {
        beginAtZero: true,
        ticks: { font: { size: 10 }, precision: 0, stepSize: 1 },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  return (
    <div style={{ height: 280 }}>
      <Bar data={chartData} options={options}/>
    </div>
  );
}
